import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useApiClient } from "../client";
import {
  InternalDashboardDto,
  InternalOrgAnalyticsDto,
  UpdateOrgNotesDto,
} from "../interfaces/internal-analytics.interface";

export const internalAnalyticsKeys = {
  all: ["internal-analytics"] as const,
  dashboard: () => [...internalAnalyticsKeys.all, "dashboard"] as const,
  organization: (id: string) => [...internalAnalyticsKeys.all, "organization", id] as const,
};

export function useInternalAnalyticsDashboard(
  options?: Omit<UseQueryOptions<InternalDashboardDto>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: internalAnalyticsKeys.dashboard(),
    queryFn: () => apiRequest<InternalDashboardDto>("/internal/analytics/dashboard"),
    ...options,
  });
}

export function useInternalAnalyticsOrganization(
  orgId: string,
  options?: Omit<UseQueryOptions<InternalOrgAnalyticsDto>, "queryKey" | "queryFn">
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: internalAnalyticsKeys.organization(orgId),
    queryFn: () => apiRequest<InternalOrgAnalyticsDto>(`/internal/analytics/organization/${orgId}`),
    enabled: !!orgId,
    ...options,
  });
}

export function useInternalAnalyticsUpdateOrgNotes(
  orgId: string,
  options?: Omit<UseMutationOptions<void, Error, UpdateOrgNotesDto>, "mutationFn">
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateOrgNotesDto) =>
      apiRequest<void>(`/internal/analytics/organization/${orgId}/notes`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: internalAnalyticsKeys.organization(orgId),
      });
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}
